import { formatApiErrorDetail } from "@/lib/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle, XCircle, ArrowLeft, Tray, MagnifyingGlass, Buildings } from "@phosphor-icons/react";
import { useT, PublicLangToggle } from "@/i18n";

const BASE = process.env.REACT_APP_BACKEND_URL;
const usd = c => `$${((c || 0) / 100).toFixed(2)}`;

function StatusPill({ status, t }) {
  const map = {
    pending: ["bg-orange-500 text-white animate-pulse", t("inbox.pending")],
    approved: ["bg-emerald-600 text-white", t("inbox.approved")],
    rejected: ["bg-red-600 text-white", t("inbox.rejected")],
  };
  const [cls, label] = map[status] || ["bg-slate-500 text-white", status];
  return <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${cls}`}>{label}</span>;
}

function InboxRow({ item, cap, selected, onToggle, onDecide, busy }) {
  const { t } = useT();
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [rowBusy, setRowBusy] = useState(false);
  const quoteNeeded = item.quote_status === "needed";
  const overCap = !quoteNeeded && item.cost_cents > cap;
  const isPending = item.approval_status === "pending";
  const decide = async (decision) => {
    setRowBusy(true);
    try {
      await onDecide([{ project_id: item.project_id, item_id: item.id }], decision, note);
    } finally { setRowBusy(false); }
  };
  return (
    <div className={`p-3 bg-white border-2 border-[#0f172a] ${isPending ? "border-l-8 border-l-orange-500" : "opacity-90"}`}
      data-testid={`inbox-row-${item.id}`}>
      <div className="flex items-start gap-2">
        {isPending && (
          <input type="checkbox" checked={selected} onChange={() => onToggle(item)}
            data-testid={`inbox-select-${item.id}`}
            className="mt-1 h-4 w-4 accent-orange-500 shrink-0"/>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 flex-wrap mb-1">
            <StatusPill status={item.approval_status} t={t}/>
            {overCap && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-100 text-red-700">{t("inbox.over_cap", { cap: cap/100 })}</span>}
            {item.discovery === "post" && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-pink-500 text-white">{t("inbox.new_find")}</span>}
            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700">{item.room}</span>
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{item.category}</span>
          </div>
          <div className="font-black text-sm truncate">{item.title}</div>
          <div className="text-[10px] text-slate-500 mt-0.5 truncate">{item.address} · {item.project_name}</div>
          {item.description && <div className="text-xs text-slate-600 italic mt-1">{item.description}</div>}
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            {quoteNeeded ? (
              <div className="font-display font-black text-base text-amber-700 bg-amber-100 px-2 py-1 border-2 border-amber-500 uppercase tracking-widest">{t("inbox.quote_needed")}</div>
            ) : (
              <div className={`font-display font-black text-xl ${isPending ? "text-orange-600" : "text-slate-700"}`}>{usd(item.cost_cents)}</div>
            )}
            {item.share_token && (
              <a href={`/project/${item.share_token}`} target="_blank" rel="noreferrer"
                className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900">{t("inbox.open_project")}</a>
            )}
            {item.approver_note && !isPending && (
              <div className="text-[10px] italic text-slate-500 truncate">{t("inbox.note_prefix")} "{item.approver_note}"</div>
            )}
          </div>
          {isPending && showNote && (
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
              placeholder={t("inbox.note_placeholder")}
              data-testid={`inbox-note-${item.id}`}
              className="mt-2 w-full px-2 py-1 border-2 border-slate-300 text-xs resize-none"/>
          )}
          {isPending && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <button onClick={() => decide("approved")} disabled={rowBusy || busy} data-testid={`inbox-approve-${item.id}`}
                className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <CheckCircle weight="bold" size={12}/> {t("inbox.approve")}
              </button>
              <button onClick={() => decide("rejected")} disabled={rowBusy || busy} data-testid={`inbox-reject-${item.id}`}
                className="h-9 px-3 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <XCircle weight="bold" size={12}/> {t("inbox.reject")}
              </button>
              <button onClick={() => setShowNote(v => !v)}
                className="h-9 px-3 bg-white hover:bg-slate-50 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest">
                {showNote ? t("inbox.note_hide") : t("inbox.note_show")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LandlordInbox() {
  const { t } = useT();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [addressFilter, setAddressFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState("");

  const STATUS_TABS = [
    { id: "pending",  label: t("inbox.tab.pending"),  accent: "orange"  },
    { id: "approved", label: t("inbox.tab.approved"), accent: "emerald" },
    { id: "rejected", label: t("inbox.tab.rejected"), accent: "red"     },
    { id: "all",      label: t("inbox.tab.all"),      accent: "slate"   },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status });
      if (addressFilter) params.set("address_key", addressFilter);
      const { data } = await axios.get(`${BASE}/api/public/landlord/${token}/inbox?${params}`);
      setData(data);
      setSelected(new Set());
    } catch { toast.error(t("inbox.expired_toast")); }
    finally { setLoading(false); }
  }, [token, status, addressFilter, t]);
  useEffect(() => { load(); }, [load]);

  const decide = async (rows, decision, note = "") => {
    try {
      const { data: r } = await axios.post(`${BASE}/api/public/landlord/${token}/decide-bulk`,
        { items: rows, decision, note });
      const label = decision === "approved" ? t("inbox.approved") : t("inbox.rejected");
      toast.success(t("inbox.decided", { label, n: r.updated, skipped: r.skipped.length }));
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? t("inbox.decide_fail"))); }
  };
  const bulkDecide = async (decision) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const rows = Array.from(selected).map(id => {
        const it = (data?.items || []).find(x => x.id === id);
        return it ? { project_id: it.project_id, item_id: it.id } : null;
      }).filter(Boolean);
      await decide(rows, decision, bulkNote);
      setBulkNote("");
    } finally { setBulkBusy(false); }
  };
  const toggle = (item) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(item.id)) n.delete(item.id); else n.add(item.id);
      return n;
    });
  };

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    if (!q) return data.items;
    const needle = q.toLowerCase();
    return data.items.filter(it =>
      it.title.toLowerCase().includes(needle) ||
      it.address.toLowerCase().includes(needle) ||
      it.room.toLowerCase().includes(needle) ||
      it.category.toLowerCase().includes(needle) ||
      it.description.toLowerCase().includes(needle));
  }, [data, q]);

  const selectAllVisible = () => {
    const ids = filtered.filter(x => x.approval_status === "pending").map(x => x.id);
    setSelected(new Set(ids));
  };

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-400 text-xs">{t("common.loading")}</div>;
  if (!data) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-500">{t("inbox.invalid_link")}</div>;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="landlord-inbox">
      <Toaster richColors position="top-center"/>
      <header className="bg-[#0f172a] text-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <Link to={`/landlord/${token}`} className="text-[10px] font-black uppercase tracking-widest text-amber-500 hover:text-amber-400 inline-flex items-center gap-1">
              <ArrowLeft weight="bold" size={12}/> {t("inbox.portfolio")}
            </Link>
            <PublicLangToggle/>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Tray weight="fill" size={26} className="text-amber-500"/>
            <div>
              <div className="font-display font-black text-2xl leading-tight uppercase tracking-tight">{t("inbox.title")}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-300">{data.landlord_name}</div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] font-black uppercase tracking-widest text-orange-400">{t("inbox.waiting_on_you")}</div>
              <div className="font-display font-black text-2xl leading-none" data-testid="inbox-total-pending">
                {data.count_pending}
                <span className="text-sm text-slate-300 ml-2">· {usd(data.total_pending_cost_cents)}</span>
              </div>
              {data.count_awaiting_quote > 0 && (
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-300 mt-0.5" data-testid="inbox-awaiting-quote">
                  {t("inbox.awaiting_quote", { n: data.count_awaiting_quote })}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {(data.per_property || []).length > 1 && (
          <section data-testid="per-property-stats" className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button onClick={() => setAddressFilter("")}
              data-testid="per-property-all"
              className={`text-left p-2 border-2 ${addressFilter === "" ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-white hover:bg-slate-50"}`}>
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Buildings weight="bold" size={10}/> {t("inbox.all_properties")}</div>
              <div className="font-display font-black text-lg leading-tight">{data.count}</div>
            </button>
            {data.per_property.map(p => (
              <button key={p.address} onClick={() => setAddressFilter(p.address.toLowerCase())}
                data-testid={`per-property-${p.address.toLowerCase().replace(/\s+/g,'-')}`}
                className={`text-left p-2 border-2 ${addressFilter === p.address.toLowerCase() ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-white hover:bg-slate-50"}`}>
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 truncate">{p.address}</div>
                <div className="font-display font-black text-lg leading-tight">
                  {p.pending}
                  {p.pending_cost_cents > 0 && <span className="text-xs text-slate-500 ml-1">· {usd(p.pending_cost_cents)}</span>}
                </div>
                {p.awaiting_quote > 0 && (
                  <div className="text-[9px] font-black uppercase tracking-widest text-amber-700 mt-0.5">{t("inbox.awaiting_quote", { n: p.awaiting_quote })}</div>
                )}
              </button>
            ))}
          </section>
        )}

        <section className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 border-2 border-[#0f172a] bg-white">
            {STATUS_TABS.map(tab => (
              <button key={tab.id} onClick={() => setStatus(tab.id)} data-testid={`inbox-tab-${tab.id}`}
                className={`px-3 h-9 font-black uppercase text-[10px] tracking-widest ${status === tab.id ? `bg-${tab.accent}-500 text-white` : "hover:bg-slate-100"}`}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass weight="bold" size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t("inbox.search")}
              data-testid="inbox-search"
              className="w-full h-9 pl-7 pr-3 border-2 border-slate-300 text-sm"/>
          </div>
        </section>

        {status === "pending" && filtered.some(x => x.approval_status === "pending") && (
          <section data-testid="inbox-bulk-bar" className="p-2 bg-white border-2 border-[#0f172a] flex flex-wrap items-center gap-2 sticky top-16 z-20">
            <button onClick={selectAllVisible}
              data-testid="inbox-select-all"
              className="h-9 px-3 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest bg-white hover:bg-slate-50">
              {t("inbox.select_all", { n: filtered.filter(x => x.approval_status === "pending").length })}
            </button>
            {selected.size > 0 && (
              <button onClick={() => setSelected(new Set())}
                className="h-9 px-3 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest bg-white hover:bg-slate-50">
                {t("inbox.clear", { n: selected.size })}
              </button>
            )}
            <input value={bulkNote} onChange={e => setBulkNote(e.target.value)}
              placeholder={t("inbox.bulk_note")}
              data-testid="inbox-bulk-note"
              className="flex-1 min-w-[160px] h-9 px-2 border-2 border-slate-300 text-xs"/>
            <button onClick={() => bulkDecide("approved")} disabled={selected.size === 0 || bulkBusy}
              data-testid="inbox-bulk-approve"
              className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
              <CheckCircle weight="bold" size={12}/> {selected.size > 0 ? t("inbox.approve_n", { n: selected.size }) : t("inbox.approve")}
            </button>
            <button onClick={() => bulkDecide("rejected")} disabled={selected.size === 0 || bulkBusy}
              data-testid="inbox-bulk-reject"
              className="h-9 px-3 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
              <XCircle weight="bold" size={12}/> {selected.size > 0 ? t("inbox.reject_n", { n: selected.size }) : t("inbox.reject")}
            </button>
          </section>
        )}

        <section className="space-y-2" data-testid="inbox-items">
          {filtered.length === 0 ? (
            <div className="p-8 bg-white border-2 border-dashed border-emerald-500 text-center">
              <CheckCircle weight="fill" size={44} className="mx-auto text-emerald-500"/>
              <div className="mt-2 font-black uppercase tracking-widest text-sm">
                {status === "pending" ? t("inbox.zero") : t("inbox.no_matches")}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {status === "pending"
                  ? t("inbox.zero_sub", { cap: data.approver_cap_cents / 100 })
                  : t("inbox.no_matches_sub")}
              </div>
            </div>
          ) : (
            filtered.map(it => (
              <InboxRow key={`${it.project_id}-${it.id}`} item={it} cap={data.approver_cap_cents}
                selected={selected.has(it.id)} onToggle={toggle} onDecide={decide} busy={bulkBusy}/>
            ))
          )}
        </section>

        <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 text-center pt-4">
          {t("inbox.footer")}
        </div>
      </div>
    </div>
  );
}
